import { useNavigate } from "react-router-dom";
import { Clock, CreditCard } from "lucide-react";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { Button } from "@/components/ui/button";
import { useGenieMeetingUsage } from "@/hooks/agent/useGenieMeetings";
import { Agent } from "@/types/agent/agent";

interface UsageBillingCardProps {
  agent: Agent;
  brand?: string;
}

const MeetingMinutesUsage = ({ agent, brand }: { agent: Agent; brand: string }) => {
  const { data: usage, isLoading } = useGenieMeetingUsage(agent.id);
  const percentUsed = usage?.minutesCap ? Math.min(100, (usage.minutesUsed / usage.minutesCap) * 100) : 0;
  const capReached = !!usage && usage.minutesUsed >= usage.minutesCap;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Clock className="w-5 h-5" style={{ color: brand }} />
        <h3 className="text-base font-semibold text-gray-900">Meeting Minutes</h3>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Used this month</label>
        {isLoading || !usage ? (
          <div className="h-8 w-32 rounded-md bg-gray-100 animate-pulse" />
        ) : (
          <div className="text-2xl font-bold text-gray-900">
            {usage.minutesUsed}
            <span className="text-base font-medium text-gray-500"> / {usage.minutesCap} min</span>
          </div>
        )}
        <div className="mt-3 h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full transition-[width] duration-700 ease-out"
            style={{ width: `${percentUsed}%`, backgroundColor: brand }}
          />
        </div>
      </div>
      <p className="text-sm text-gray-600">
        {capReached
          ? "This month's meeting minutes are used up. Genies can join meetings again on the 1st."
          : "Shared by every genie on your account and reset on the 1st of each month. Recordings made on the genie's own page don't count."}
      </p>
    </div>
  );
};

export const UsageBillingCard = ({ agent, brand = "#4E9CFF" }: UsageBillingCardProps) => {
  const navigate = useNavigate();
  const isListener = agent.type === "listener";

  return (
    <OverviewSectionCard
      title="Usage & Billing"
      subtitle={isListener ? "Meeting minutes used this month" : "Manage call limits and purchases"}
      genieIcon="gauge"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
    >
      {isListener ? (
        <MeetingMinutesUsage agent={agent} brand={brand} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            <h3 className="text-base font-semibold text-gray-900">Call Limit & Usage</h3>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Daily Call Limit</label>
            <div className="text-2xl font-bold text-gray-900">{agent.call_limit ?? "Not set"}</div>
          </div>
          <p className="text-sm text-gray-600">
            Manage your call limits and purchase additional calls in the Account Settings page.
          </p>
          <Button
            onClick={() => navigate("/genie-hub/account-settings?tab=call-purchases")}
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold"
          >
            Go to Account Settings
          </Button>
        </div>
      )}
    </OverviewSectionCard>
  );
};
